// Team-Jahresübersicht — eine Zeile pro Mitarbeiter mit YTD-Saldo (sekunden-
// genau), Urlaubsstand und Krankheits-/Sonstigen Abwesenheits-Tagen. Scope
// kommt vom Backend (admin=alle, leader=eigenes Team, user=self).
import { useQuery } from '@tanstack/react-query'
import { api, type TeamYearSummaryRow } from '../lib/api'
import { cn } from '../lib/utils'

interface TeamYearTableProps {
  year: number
}

export function TeamYearTable({ year }: TeamYearTableProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'team-year-summary', year],
    queryFn: () => api.teamYearSummary({ year }),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin h-6 w-6 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-12 text-center">
        <p className="text-text-secondary">
          {(error as Error | undefined)?.message ?? 'Keine Daten verfügbar.'}
        </p>
      </div>
    )
  }

  const rows = data.rows
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-12 text-center">
        <p className="text-text-secondary">Keine Mitarbeiter im Scope.</p>
      </div>
    )
  }

  // Summen am Tabellen-Ende — schneller Überblick fürs Management.
  const totalBalance = rows.reduce((s, r) => s + r.balanceSeconds, 0)
  const totalVacationTaken = rows.reduce((s, r) => s + r.vacation.taken, 0)
  const totalVacationPlanned = rows.reduce((s, r) => s + r.vacation.planned, 0)
  const totalSick = rows.reduce((s, r) => s + r.sickDays, 0)
  const totalOther = rows.reduce((s, r) => s + r.otherAbsenceDays, 0)

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs text-text-tertiary">
          Stand <span className="font-mono tabular-nums">{formatDateDE(data.ytdEnd)}</span> ·{' '}
          {rows.length} Mitarbeiter
        </p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-border-subtle bg-bg-elevated/40">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-subtle bg-bg-elevated/60 text-[11px] uppercase tracking-wider text-text-tertiary">
              <Th align="left">Name</Th>
              <Th align="left">Nr</Th>
              <Th align="left">Team</Th>
              <Th align="right">Saldo YTD</Th>
              <Th align="right">Anspruch</Th>
              <Th align="right">Genommen</Th>
              <Th align="right">Geplant</Th>
              <Th align="right">Resturlaub</Th>
              <Th align="right">Krank</Th>
              <Th align="right">Sonstige</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <TeamRow key={r.userId} row={r} />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border-subtle bg-bg-elevated/60 text-xs font-medium">
              <td className="px-3 py-2.5" colSpan={3}>
                Summe
              </td>
              <td
                className={cn(
                  'px-3 py-2.5 text-right font-mono tabular-nums',
                  balanceColor(totalBalance),
                )}
              >
                {formatHM(totalBalance)}
              </td>
              <td className="px-3 py-2.5" />
              <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                {fmtDays(totalVacationTaken)}
              </td>
              <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                {fmtDays(totalVacationPlanned)}
              </td>
              <td className="px-3 py-2.5" />
              <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                {fmtDays(totalSick)}
              </td>
              <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                {fmtDays(totalOther)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

function TeamRow({ row }: { row: TeamYearSummaryRow }) {
  const restWarn = row.vacation.remaining != null && row.vacation.remaining <= 5
  return (
    <tr className="border-b border-border-subtle last:border-0 hover:bg-bg-overlay/40 transition-colors">
      <td className="px-3 py-2.5 font-medium text-text-primary">{row.name}</td>
      <td className="px-3 py-2.5 text-text-tertiary font-mono tabular-nums">
        {row.personnelNumber ?? '—'}
      </td>
      <td className="px-3 py-2.5 text-text-secondary">{row.teamName ?? '—'}</td>
      <td
        className={cn(
          'px-3 py-2.5 text-right font-mono tabular-nums',
          balanceColor(row.balanceSeconds),
        )}
        title={`Ist ${formatHM(row.actualSeconds)} − Soll ${formatHM(row.targetSeconds)}`}
      >
        {formatHM(row.balanceSeconds)}
      </td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-text-secondary">
        {row.vacation.entitled != null ? fmtDays(row.vacation.entitled) : '—'}
      </td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums">
        {fmtDays(row.vacation.taken)}
      </td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-text-secondary">
        {fmtDays(row.vacation.planned)}
      </td>
      <td
        className={cn(
          'px-3 py-2.5 text-right font-mono tabular-nums',
          row.vacation.remaining == null
            ? 'text-text-tertiary'
            : restWarn
              ? 'text-warning'
              : 'text-text-primary',
        )}
      >
        {row.vacation.remaining != null ? fmtDays(row.vacation.remaining) : '—'}
      </td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-text-secondary">
        {fmtDays(row.sickDays)}
      </td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-text-secondary">
        {fmtDays(row.otherAbsenceDays)}
      </td>
    </tr>
  )
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th className={cn('px-3 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}>
      {children}
    </th>
  )
}

function formatHM(seconds: number): string {
  const sign = seconds < 0 ? '−' : seconds > 0 ? '+' : ''
  const abs = Math.abs(seconds)
  const h = Math.floor(abs / 3600)
  const m = Math.floor((abs % 3600) / 60)
  return `${sign}${h}:${String(m).padStart(2, '0')}`
}

function fmtDays(d: number): string {
  if (Number.isInteger(d)) return String(d)
  return d.toFixed(1).replace('.', ',')
}

function balanceColor(seconds: number): string {
  // Toleranz wie im Kalender Soll/Ist-Strip: ±15min neutral, danach grün/rot.
  if (Math.abs(seconds) < 15 * 60) return 'text-text-secondary'
  return seconds > 0 ? 'text-success' : 'text-danger'
}

function formatDateDE(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}
